/**
 * TimeAdmin — Time & Expenses approvals for admins/managers (/time).
 * Pending queue (managers see only their assigned employees), approve/reject,
 * receipt viewing, payroll CSV export, settings (pay period, mileage rate),
 * and employee setup (PIN + assigned manager, admin only).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Clock4, Download, Check, X, MapPin, ReceiptText, TriangleAlert, Printer } from "lucide-react";

export default function TimeAdmin() {
  const [pending, setPending] = useState({ time: [], expenses: [], time_off: [] });
  const [balances, setBalances] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [emps, setEmps] = useState([]);
  const [users, setUsers] = useState([]);
  const [range, setRange] = useState(() => {
    const d = new Date(); const day = (d.getDay() + 6) % 7;
    const mon = new Date(d); mon.setDate(d.getDate() - day);
    return { start: mon.toISOString().slice(0, 10), end: new Date().toISOString().slice(0, 10) };
  });
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";

  const load = () => {
    api.get("/timeclock/pending").then((r) => setPending(r.data)).catch(() => {});
    api.get("/timeclock/balances").then((r) => setBalances(r.data || [])).catch(() => {});
    api.get("/settings/timeclock").then((r) => setCfg(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/employees").then((r) => setEmps(r.data || [])).catch(() => {});
      api.get("/users").then((r) => setUsers((r.data || []).filter((u) => ["admin", "manager"].includes(u.role)))).catch(() => {});
    }
  };
  useEffect(load, [isAdmin]); // eslint-disable-line

  const review = async (action, timeIds, expIds, offIds = []) => {
    let reason = "";
    if (action === "reject") {
      reason = window.prompt("Reason for rejection (the employee sees this):") || "";
      if (!reason) return;
    }
    try {
      await api.post("/timeclock/review", { action, reason, time_ids: timeIds, expense_ids: expIds, timeoff_ids: offIds });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const byEmp = useMemo(() => {
    const g = {};
    for (const t of pending.time) (g[t.employee_name] = g[t.employee_name] || { time: [], expenses: [], off: [] }).time.push(t);
    for (const x of pending.expenses) (g[x.employee_name] = g[x.employee_name] || { time: [], expenses: [], off: [] }).expenses.push(x);
    for (const o of pending.time_off || []) (g[o.employee_name] = g[o.employee_name] || { time: [], expenses: [], off: [] }).off.push(o);
    return g;
  }, [pending]);

  const exportCsv = async () => {
    const r = await api.get(`/timeclock/export.csv?start=${range.start}&end=${range.end}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `payroll_${range.start}_${range.end}.csv`;
    a.click();
  };

  const viewReceipt = async (id) => {
    const r = await api.get(`/timeclock/receipt/${id}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };

  const saveSetup = async (empId, patch) => {
    try { await api.put(`/timeclock/employee-setup/${empId}`, patch); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="time-admin-page">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Clock4 className="w-6 h-6 text-blue-700" />
        <h1 className="font-heading text-2xl font-black tracking-tight">Time &amp; Expenses</h1>
        <div className="ml-auto flex items-center gap-2">
          <a href="/timeclock-guide.pdf" target="_blank" rel="noreferrer" data-testid="print-crew-instructions" className="h-9 px-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border border-blue-700 text-blue-700 rounded-sm hover:bg-blue-50">
            <Printer className="w-3 h-3" /> Print Crew Instructions
          </a>
          <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-9 px-2 border border-zinc-300 rounded-sm text-xs" data-testid="export-start" />
          <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-9 px-2 border border-zinc-300 rounded-sm text-xs" data-testid="export-end" />
          <button onClick={exportCsv} data-testid="payroll-export" className="h-9 px-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-zinc-950 text-white rounded-sm hover:bg-zinc-800">
            <Download className="w-3 h-3" /> Payroll CSV
          </button>
        </div>
      </div>

      {Object.keys(byEmp).length === 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500 mb-6" data-testid="no-pending">
          Nothing waiting for approval. Crew submits from the Time Clock page: <span className="font-mono text-xs">{window.location.origin}/timeclock</span>
        </div>
      )}

      {Object.entries(byEmp).map(([name, g]) => {
        const hrs = g.time.reduce((s, t) => s + Number(t.hours || 0), 0);
        const amt = g.expenses.reduce((s, x) => s + Number(x.amount || 0), 0);
        return (
          <div key={name} className="bg-white border border-zinc-200 rounded-sm mb-4" data-testid={`pending-${name}`}>
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-3">
              <div className="font-bold">{name}</div>
              <div className="text-xs text-zinc-500">{hrs.toFixed(2)} hrs · {formatCurrency(amt)} expenses</div>
              <div className="ml-auto flex gap-2">
                <button onClick={() => review("approve", g.time.map((t) => t.id), g.expenses.map((x) => x.id), g.off.map((o) => o.id))} data-testid={`approve-${name}`} className="h-8 px-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white rounded-sm hover:bg-emerald-700"><Check className="w-3 h-3" /> Approve All</button>
                <button onClick={() => review("reject", g.time.map((t) => t.id), g.expenses.map((x) => x.id), g.off.map((o) => o.id))} data-testid={`reject-${name}`} className="h-8 px-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border border-red-300 text-red-700 rounded-sm hover:bg-red-50"><X className="w-3 h-3" /> Reject</button>
              </div>
            </div>
            <div className="px-4 py-2 text-xs space-y-1">
              {g.time.map((t) => (
                <div key={t.id} className="flex items-center gap-2 text-zinc-700">
                  <span className="font-mono">{t.clock_in.slice(0, 10)}</span>
                  <span>{t.project_title}</span>
                  <span className="font-mono font-bold">{Number(t.hours).toFixed(2)}h</span>
                  {t.gps_in ? (
                    <a href={`https://maps.google.com/?q=${t.gps_in.lat},${t.gps_in.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-blue-700 hover:underline"><MapPin className="w-3 h-3" /> map</a>
                  ) : (
                    <span className="text-amber-600 font-bold text-[10px] uppercase">no location</span>
                  )}
                </div>
              ))}
              {g.off.map((o) => (
                <div key={o.id}>
                  <div className="flex items-center gap-2 text-rose-700">
                    <span className="font-mono">{o.start_date}{o.end_date !== o.start_date ? ` → ${o.end_date}` : ""}</span>
                    <span className="font-bold uppercase text-[10px]">{o.type}</span>
                    <span className="font-mono font-bold">{Number(o.hours).toFixed(0)}h</span>
                    {o.note && <span className="text-zinc-500">· {o.note}</span>}
                  </div>
                  {(o.conflicts || []).map((c, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-amber-700 pl-4 mt-0.5" data-testid={`pto-conflict-${o.id}-${i}`}>
                      <TriangleAlert className="w-3 h-3 shrink-0" />
                      <span className="font-bold text-[10px] uppercase tracking-wider">Job conflict:</span>
                      <span>{c.title} is scheduled {c.start} → {c.end}</span>
                    </div>
                  ))}
                </div>
              ))}
              {g.expenses.map((x) => (
                <div key={x.id} className="flex items-center gap-2 text-zinc-500">
                  <span className="font-mono">{x.expense_date}</span>
                  <span>{x.category}{x.miles > 0 ? ` (${x.miles} mi)` : ""} · {x.description || "—"}</span>
                  <span className="font-mono font-bold">{formatCurrency(x.amount)}</span>
                  {x.category !== "Mileage" && (
                    <button onClick={() => viewReceipt(x.id)} className="inline-flex items-center gap-0.5 text-blue-700 hover:underline" data-testid={`receipt-${x.id}`}><ReceiptText className="w-3 h-3" /> receipt</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {balances.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-4 mt-6" data-testid="tc-balances-card">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Time-Off Balances (this year)</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-zinc-400"><th className="py-1">Employee</th><th>Vacation Left</th><th>Sick Available</th><th>Unpaid Used</th></tr></thead>
            <tbody>
              {balances.map((b) => (
                <tr key={b.employee_id} className="border-t border-zinc-100">
                  <td className="py-1.5 font-bold">{b.name}</td>
                  <td className="font-mono">{b.vacation_left}h <span className="text-zinc-400">/ {b.vacation_bank}h</span></td>
                  <td className="font-mono">{b.sick_left}h <span className="text-zinc-400">accrued {b.sick_accrued}h</span></td>
                  <td className="font-mono">{b.unpaid_used}h</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] text-zinc-400 mt-2">Vacation: 40h after 1 yr · 80h after 5 · 120h after 10 (per hire date). Sick: CO accrual 1h per 30 worked, 48h cap. Federal holidays auto-add 8h on the payroll export.</div>
        </div>
      )}

      {cfg && isAdmin && (
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="tc-settings">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Payroll Settings</div>
            <label className="text-xs font-bold text-zinc-700 block mb-1">Pay period</label>
            <select value={cfg.pay_period} onChange={(e) => setCfg({ ...cfg, pay_period: e.target.value })} data-testid="pay-period" className="h-9 px-2 border border-zinc-300 rounded-sm text-sm w-full mb-3">
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
            </select>
            <label className="text-xs font-bold text-zinc-700 block mb-1">Mileage rate ($/mi)</label>
            <input type="number" step="0.01" value={cfg.mileage_rate} onChange={(e) => setCfg({ ...cfg, mileage_rate: e.target.value })} data-testid="mileage-rate" className="h-9 px-2 border border-zinc-300 rounded-sm text-sm w-full mb-3" />
            <label className="text-xs font-bold text-zinc-700 block mb-1">Labor burden multiplier</label>
            <input type="number" step="0.05" value={cfg.burden_multiplier ?? 1.35} onChange={(e) => setCfg({ ...cfg, burden_multiplier: e.target.value })} data-testid="burden-multiplier" className="h-9 px-2 border border-zinc-300 rounded-sm text-sm w-full mb-3" />
            <button onClick={async () => { await api.put("/settings/timeclock", cfg); toast.success("Settings saved"); }} data-testid="tc-settings-save" className="h-9 px-4 text-[10px] font-bold uppercase tracking-wider bg-zinc-950 text-white rounded-sm">Save</button>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="tc-emp-setup">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Employee Setup — PIN &amp; Manager</div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {emps.map((e) => (
                <div key={e.id} className="grid grid-cols-[1fr_80px_1fr] gap-2 items-center text-xs">
                  <div className="font-bold truncate">{e.name}</div>
                  <input defaultValue={e.timeclock_pin || ""} placeholder="PIN" maxLength={8}
                    onBlur={(ev) => ev.target.value !== (e.timeclock_pin || "") && saveSetup(e.id, { timeclock_pin: ev.target.value })}
                    data-testid={`pin-${e.id}`} className="h-8 px-2 border border-zinc-300 rounded-sm font-mono" />
                  <select defaultValue={e.manager_user_id || ""} onChange={(ev) => saveSetup(e.id, { manager_user_id: ev.target.value })} data-testid={`mgr-${e.id}`} className="h-8 px-1 border border-zinc-300 rounded-sm">
                    <option value="">No manager (admins only)</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                  </select>
                </div>
              ))}
              {emps.length === 0 && <div className="text-xs text-zinc-500">Add employees under Contacts → Employees first.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
